import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Text } from "@performyard/py-components";
import {
	MetaDataSurveyInsightResponseStream,
	SurveyInsightResponseStreamJson,
	UserErrorResponseJson,
} from "@performyard/service-contracts";

import { useSurvey } from "../../../pages/Surveys/pages/SurveyDetails/hooks/useSurvey";

import { useAiQuery } from "../../../api/hooks";

import FormattedDateTime from "../../FormattedDateTime";
import { AISidePanel } from "../SidePanel";

type SurveyInsightModuleProps = {
	surveyId: string;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
};

export const SurveyInsightModule = ({ surveyId, isOpen, onOpenChange }: SurveyInsightModuleProps) => {
	const [recreateCount, setRecreateCount] = useState(0);
	const [retryIsProcessing, setRetryIsProcessing] = useState(false);
	const { survey } = useSurvey({ surveyId });

	const { data, isLoading, isFetching, isRefetching } = useAiQuery<
		MetaDataSurveyInsightResponseStream | UserErrorResponseJson
	>({
		type: "survey-insight",
		surveyId,
		findAdditionalContext: false,
		retry: recreateCount > 0 ? "true" : "false",
		responseStyle: "structured",
		recreateCount,
	});

	const inProgress = useMemo(() => {
		if (isLoading || isRefetching || retryIsProcessing) return true;
		if (data && typeof data === "object" && "status" in data) {
			return data.status === "in_progress";
		}
		return false;
	}, [data, isLoading, isRefetching, retryIsProcessing]);

	const isError = (
		response: UserErrorResponseJson | MetaDataSurveyInsightResponseStream | undefined
	): response is UserErrorResponseJson => {
		if (!response || inProgress || retryIsProcessing) return false;
		// if response is type MetaDataEngagementInsightResponseStream then return false
		if (response && typeof response === "object" && "status" in response) {
			return false;
		}
		// if response is type UserErrorResponse then return true
		if (
			response &&
			typeof response === "object" &&
			"data" in response &&
			typeof (response as { data?: unknown }).data === "object" &&
			response.data !== null &&
			"message" in (response.data as Record<string, unknown>)
		) {
			console.error(response);
			response.message = "The generation of Survey Insights was unsuccessful. Please try again.";
			return true;
		}
		return true;
	};

	const handleRetry = useCallback(() => {
		setRetryIsProcessing(true);
		setRecreateCount((prev) => prev + 1);
	}, []);

	useEffect(() => {
		if (!isFetching && retryIsProcessing) {
			setRetryIsProcessing(false);
		}
	}, [isFetching, retryIsProcessing]);

	const formatSurveyDateString = () => {
		if (survey?.startDate && survey?.endDate) {
			return (
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					<span className="flex flex-row items-center gap-1">
						Start and End Date:
						<Text size="paragraph-3" weight="regular" overrideElement="span" color="neutral-9">
							<span className="flex flex-row items-center">
								<FormattedDateTime date={new Date(survey.startDate)} dateSize="large" hasTime={false} />
								-
								<FormattedDateTime date={new Date(survey.endDate)} dateSize="large" hasTime={false} />
							</span>
						</Text>
					</span>
				</Text>
			);
		} else if (survey?.startDate) {
			return (
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					<span className="flex flex-row items-center gap-1">
						Start Date:
						<Text size="paragraph-3" weight="regular" overrideElement="span" color="neutral-9">
							<FormattedDateTime date={new Date(survey.startDate)} dateSize="large" hasTime={false} />
						</Text>
					</span>
				</Text>
			);
		}
		return null;
	};

	const renderHeader = (data: SurveyInsightResponseStreamJson) => (
		<div className="border-radius-1 flex w-full flex-col items-center justify-center gap-4 self-stretch border-b border-solid border-neutral-200 pb-4">
			<div className="flex items-start gap-5 self-stretch rounded bg-neutral-100 p-2">
				<div className="flex flex-1 shrink-0 basis-0 flex-col items-start gap-2">
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
						{data.title}
					</Text>
					{formatSurveyDateString()}
				</div>
			</div>
		</div>
	);

	const renderInfoSection = (data: SurveyInsightResponseStreamJson) => (
		<div className="flex flex-col gap-1">
			<Text size="caption" weight="regular" color="neutral-9">
				Insights Populated: {format(new Date(data.createdAt ?? Date.now()), "MMMM d, yyyy")}
				{data.createdBy && ` by ${data.createdBy}`}
			</Text>
			{survey?.status !== "closed" && (
				<Text size="caption" weight="regular" color="neutral-9">
					**Generated from incomplete survey results
				</Text>
			)}
		</div>
	);

	return (
		<AISidePanel<MetaDataSurveyInsightResponseStream>
			type="survey"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			data={data && !isError(data) ? data : null}
			isLoading={inProgress}
			renderHeader={renderHeader}
			renderInfoSection={renderInfoSection}
			onRetry={handleRetry}
			error={data && isError(data) ? data : null}
			logoLoaderProps={{
				textArray: [
					"Retrieving Survey Participants…",
					"Compiling Responses…",
					"Organizing Responses…",
					"Processing Responses…",
					"Evaluating Summarization…",
					"Structuring Summary Format…",
					"Finalizing Survey Results…",
					"Finishing Up…",
				],
				mainText: "Processing Survey Insights",
			}}
		/>
	);
};
